import { getDepositSecuresText } from 'dpl/components/StripeCharge/New/StripeChargeModal/util';
import type { CheckoutFaq } from 'dpl/payments/components/checkout/CheckoutHelpSection';
import { PAYMENT_REQUEST_CATEGORIES } from 'dpl/shared/constants/breeder_app';
import { capitalize } from 'dpl/shared/utils';

// The fields the Edit payment sheet drafts and saves back together.
export interface EditablePaymentFields {
  amount_in_dollars: number | string;
  category: string;
  personal_message: string;
  litter_id: number | null;
  dog_id: number | null;
  _pickupLocationValue: string | null;
  pickup_location_attributes: Record<string, any> | null;
}

export const EDITABLE_FIELDS = [
  'amount_in_dollars',
  'category',
  'personal_message',
  'litter_id',
  'dog_id',
  '_pickupLocationValue',
  'pickup_location_attributes'
] as const satisfies readonly (keyof EditablePaymentFields)[];

export function getNextStepText({
  category,
  breederFirstName,
  depositSecures,
  dogName,
  finalPaymentTiming
}: {
  category: string;
  breederFirstName: string;
  depositSecures: string | null;
  dogName?: string;
  finalPaymentTiming: string | null;
}) {
  if (category !== PAYMENT_REQUEST_CATEGORIES.DEPOSIT) {
    return `${breederFirstName} reviews and confirms your payment, and you both get a confirmation.`;
  }

  // A spot isn't a puppy yet, so these say what happens before the pick.
  if (depositSecures === 'litter_spot') {
    return `Your spot is saved. ${breederFirstName} will reach out when it's your turn to pick.`;
  }

  if (depositSecures === 'waitlist_spot') {
    return "Your spot is saved. We'll let you know when the litter arrives and it's time to pick.";
  }

  // "A specific puppy is reserved for you" reads badly before a dog is
  // picked, so that case says "your puppy" instead.
  const secures =
    depositSecures === 'specific_puppy' && !dogName
      ? 'your puppy'
      : getDepositSecuresText(depositSecures, dogName);
  const reminder = finalPaymentTiming
    ? " We'll remind you before the balance is due."
    : '';

  if (!secures) {
    return `${breederFirstName} reviews your deposit, and you both get a confirmation.${reminder}`;
  }

  return `${capitalize(
    secures
  )} is reserved for you, and you and ${breederFirstName} both get a confirmation.${reminder}`;
}

export function getCheckoutFaqs({
  isDogPayment,
  klarnaEnabled
}: {
  isDogPayment: boolean;
  klarnaEnabled: boolean;
}): CheckoutFaq[] {
  return [
    {
      question: 'What payment methods can I use?',
      answer:
        'You can pay by credit or debit card, Apple Pay, Google Pay, or bank transfer. Credit card payments have a 3.3% processing fee.'
    },
    klarnaEnabled && {
      question: 'Can I pay over time?',
      answer:
        'Yes! With Klarna, you can split your payment into four interest-free installments on eligible purchases under $2,000 or choose monthly financing.'
    },
    isDogPayment && {
      question: 'Why do you need pick up location?',
      answer:
        "We use your puppy's pickup location to calculate sales tax. Sales tax applies to the full purchase price."
    },
    {
      question: 'Is my payment protected?',
      answer:
        "Payments made through Good Dog are secure and covered by Good Dog's Payment Protection."
    },
    {
      question: 'What if I need a refund?',
      answer:
        "Refunds are subject to your breeder's policy. Please reach out to your breeder for more information."
    }
  ].filter(Boolean) as CheckoutFaq[];
}
